import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

// PrimeNG
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageModule } from 'primeng/message';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';

// Kanzi
import * as fromCustomer from '@kanzi-apes/kanzi-customer';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { UbicacionesQueriesService } from '../../../queries/ubicaciones-queries.service';
import { UbicacionFilterModel } from '../../../queries/locations-query-keys';
import { Store } from '@ngrx/store';

/**
 * Ubicación List Component - Hierarchical tree view
 *
 * Displays ubicaciones in a hierarchical TreeTable structure:
 * WAREHOUSE → SALON → ROW → FRONT/BACK → RACK → LEVEL → POSITION
 *
 * Features:
 * - TreeTable with expand/collapse
 * - Search by name
 * - Filter by location type
 * - CRUD operations at any level
 * - Hierarchical path display
 * - Prefetch on row hover
 */
/** Comma-separated location type names considered root nodes of a customer tree. */
const ROOT_TYPE_NAMES = 'WAREHOUSE,STORE';

/** A single step in the drill-down breadcrumb path. */
interface BreadcrumbNode {
  id: number;
  name: string;
  type: string;
}

@Component({
    selector: 'kanzi-apes-ubicacion-list',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DropdownModule,
        TooltipModule,
        SkeletonModule,
        MessageModule,
        ConfirmDialogModule,
        BreadcrumbModule,
        ToastModule,
        KanziUiModule,
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './ubicacion-list.component.html',
    styleUrls: ['./ubicacion-list.component.scss']
})
export class UbicacionListComponent {
  private router = inject(Router);
  private queries = inject(UbicacionesQueriesService);
  private confirmService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private store = inject(Store);
  customerSelected = this.store.selectSignal(fromCustomer.getCustomerSelected);

  // Filters (apply within the current level)
  searchText = signal('');
  selectedType = signal<number | null>(null);

  // Pagination
  first = signal(0);
  rows = signal(10);

  // Drill-down navigation state
  // null = root level (lists the customer's WAREHOUSE/STORE root nodes)
  currentParentId = signal<number | null>(null);
  // Real path walked by the user; any length, not fixed to 7 levels
  breadcrumbPath = signal<BreadcrumbNode[]>([]);

  // Signal para controlar cuándo ejecutar las queries
  isQueryEnabled = signal<boolean>(false);

  customerEffect = effect(
    () => {
      const customer = this.customerSelected();
      if (customer) {
        // Habilitar queries solo después de tener customer
        this.isQueryEnabled.set(true);
        // Reset navigation to root when the active customer changes
        this.currentParentId.set(null);
        this.breadcrumbPath.set([]);
        this.first.set(0);
      }
    },
    { allowSignalWrites: true }
  );

  // Build reactive filters for the current level.
  // Root level (currentParentId === null) lists all root nodes (WAREHOUSE/STORE) of the customer.
  // Deeper levels filter by parent_id. Search/type apply within the current level.
  filters = computed<UbicacionFilterModel>(() => {
    const parentId = this.currentParentId();
    const base: UbicacionFilterModel = {
      search: this.searchText() || undefined,
      type_id: this.selectedType() ?? undefined,
      customer_id: this.customerSelected()?.id ?? undefined,
      page: Math.floor(this.first() / this.rows()) + 1,
      page_size: this.rows(),
    };

    if (parentId === null) {
      // Root level: list all root nodes of the customer without assuming a single warehouse.
      return { ...base, type__name: ROOT_TYPE_NAMES };
    }

    // Deeper level: direct children of the current parent.
    return { ...base, parent_id: parentId };
  });

  // Queries: always enabled once a customer exists (table always shows the current level)
  listQuery = this.queries.useUbicacionChildren(
    this.filters,
    this.isQueryEnabled
  );
  typesQuery = this.queries.useLocationTypes(signal({}));
  deleteMutation = this.queries.useDeleteUbicacion();

  // Table data
  tableData = computed(() => {
    const data = this.listQuery.data();
    return data?.results || [];
  });

  // Total records for pagination
  totalRecords = computed(() => {
    const data = this.listQuery.data();
    return data?.count || 0;
  });

  // Types for dropdown
  locationTypes = computed(() => {
    const results = this.typesQuery.data()?.results || [];
    // Exclude WAREHOUSE type from filter (it's the root)
    return results
      .filter((t) => t.name !== 'WAREHOUSE')
      .map((t) => ({ label: t.name, value: t.id }));
  });

  // Loading states
  isLoading = computed(() => {
    return (
      this.isQueryEnabled() &&
      (this.listQuery.isPending() || this.listQuery.isFetching())
    );
  });
  isTypesLoading = computed(() => this.typesQuery.isPending());

  // True when navigating below the root level (enables the "back to root" breadcrumb home)
  isRootLevel = computed(() => this.currentParentId() === null);

  // PrimeNG breadcrumb "home" item points back to the customer root level
  breadcrumbHome = computed<MenuItem>(() => ({
    icon: 'pi pi-home',
    label: this.customerSelected()?.name ?? '',
    // index -1 identifies the root/home item in onBreadcrumbItemClick
    state: { index: -1 },
  }));

  // PrimeNG breadcrumb items built from the real navigation path
  breadcrumbItems = computed<MenuItem[]>(() =>
    this.breadcrumbPath().map((node, index) => ({
      label: node.name,
      state: { index },
    }))
  );

  // Table columns
  cols = [
    { field: 'name', header: 'locations.name', width: '20%' },
    { field: 'display_name', header: 'locations.displayName', width: '20%' },
    { field: 'type', header: 'locations.type', width: '15%' },
    { field: 'external_id', header: 'locations.externalId', width: '15%' },
    { field: 'parent', header: 'locations.parent', width: '15%' },
    { field: 'actions', header: 'locations.actions', width: '15%' },
  ];

  /**
   * Prefetch ubicación detail on row hover.
   * @param location - The hovered location row
   */
  onRowHover(location: any): void {
    if (location?.id) {
      this.queries.prefetchUbicacion(location.id);
    }
  }

  /**
   * Attempts to drill down into a location's direct children (optimistic navigability).
   *
   * Navigability is decided at click time by fetching the direct-children count, never by the
   * node's `type`. If the node has children, the breadcrumb is pushed and the current parent is
   * updated to that node; otherwise a short notice is shown and no navigation happens (leaf node).
   *
   * @param location - The clicked location row to drill into
   * @returns A promise that resolves once the navigability check completes
   */
  async onDrillDown(location: any): Promise<void> {
    if (!location?.id) return;

    const count = await this.queries.fetchChildrenCount(
      location.id,
      this.customerSelected()?.id
    );

    if (count > 0) {
      this.breadcrumbPath.update((path) => [
        ...path,
        {
          id: location.id,
          name: location.name,
          type: location.type?.name ?? '',
        },
      ]);
      this.currentParentId.set(location.id);
      this.first.set(0);
      this.searchText.set('');
      this.selectedType.set(null);
    } else {
      this.messageService.add({
        severity: 'info',
        summary: 'Sin sublocaciones',
        detail: `"${location.name}" no tiene ubicaciones hijas.`,
      });
    }
  }

  /**
   * Navigates back to a previous breadcrumb level.
   *
   * Truncates the breadcrumb path up to (and including) the clicked index and sets the current
   * parent to that node. Resets pagination and level filters.
   *
   * @param index - The zero-based index of the breadcrumb node to navigate to
   */
  onBreadcrumbNavigate(index: number): void {
    const path = this.breadcrumbPath();
    if (index < 0 || index >= path.length) return;

    const target = path[index];
    this.breadcrumbPath.set(path.slice(0, index + 1));
    this.currentParentId.set(target.id);
    this.first.set(0);
    this.searchText.set('');
    this.selectedType.set(null);
  }

  /**
   * Handles a PrimeNG breadcrumb item click and routes to the matching level.
   *
   * Reads the level index stored in the item's `state`. An index of -1 (the home item) returns
   * to the customer root; any other index navigates to that breadcrumb level.
   *
   * @param event - PrimeNG breadcrumb item click event containing the clicked `item`
   */
  onBreadcrumbItemClick(event: { item: MenuItem }): void {
    const index = event?.item?.state?.['index'];
    if (index === undefined || index === null) return;

    if (index === -1) {
      this.onNavigateToRoot();
    } else {
      this.onBreadcrumbNavigate(index);
    }
  }

  /**
   * Navigates back to the root level (customer's WAREHOUSE/STORE root nodes).
   * Clears the breadcrumb path, pagination and level filters.
   */
  onNavigateToRoot(): void {
    this.breadcrumbPath.set([]);
    this.currentParentId.set(null);
    this.first.set(0);
    this.searchText.set('');
    this.selectedType.set(null);
  }

  /**
   * Handles the lazy pagination change event of the table for the current level.
   * @param event - PrimeNG lazy load event containing `first` and `rows`
   */
  onPageChange(event: any): void {
    this.first.set(event.first);
    this.rows.set(event.rows);
  }

  /**
   * Navigates to the create ubicación page.
   */
  onCreate(): void {
    this.router.navigate(['/locations/ubicaciones/create']);
  }

  /**
   * Navigates to the detail page of a location.
   * @param location - The location whose detail page will be opened
   */
  onDetail(location: any): void {
    if (location?.id) {
      this.router.navigate(['/locations/ubicaciones/detail', location.id]);
    }
  }

  /**
   * Navigates to the edit page of a location.
   * @param location - The location to edit
   */
  onEdit(location: any): void {
    if (location?.id) {
      this.router.navigate(['/locations/ubicaciones/edit', location.id]);
    }
  }

  /**
   * Deletes a location after user confirmation and refreshes the current level.
   * @param location - The location to delete
   */
  onDelete(location: any): void {
    if (!location?.id) return;

    this.confirmService.confirm({
      message: `¿Está seguro que desea eliminar la ubicación "${location.name}"?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteMutation.mutate(location.id, {
          onSuccess: () => {
            // Invalidate all ubicaciones queries, which covers the current level's
            // children query (parent_id). If the parent is left with no children,
            // it will no longer be navigable the next time it is listed.
            this.queries.invalidateAllUbicaciones();
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Ubicación eliminada correctamente',
            });
          },
          onError: (error: any) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error?.message || 'Error al eliminar la ubicación',
            });
          },
        });
      },
    });
  }

  /**
   * Clears the search and type filters applied within the current level and resets pagination.
   */
  onClearFilters(): void {
    this.searchText.set('');
    this.selectedType.set(null);
    this.first.set(0);
  }

  /**
   * Refreshes the data of the current level by invalidating the ubicaciones cache.
   */
  onRefresh(): void {
    this.queries.invalidateAllUbicaciones();
  }
}
